import { useId, useState } from "react";
import { formatTime, zoneAbbreviation } from "@helm/core";
import { formatTicks } from "@helm/ui";
import { useDisplayZone } from "../time/useDisplayZone";
import { mdEmptyReason, priceFormatOf, useMarket, useMdStatus } from "../md/marketData";

/**
 * Time & Sales (tape): newest print first; aggressor side by colour **and** ▲/▼ glyph (SPEC §17.4
 * never colour alone); minimum-size filter (owner mockup 01).
 */
export function TimeSales({ symbol }: { symbol: string | null }): React.JSX.Element {
  const id = useId();
  const m = useMarket(symbol);
  const status = useMdStatus();
  const zone = useDisplayZone();
  const [minSize, setMinSize] = useState("1");
  const fmt = priceFormatOf(m?.info ?? null);
  const min = Math.max(1, Number(minSize) || 1);
  const prints = (m?.prints ?? []).filter((p) => p.size >= min).slice(0, 200);

  return (
    <div className="tape">
      <div className="tape-head">
        <span className="dom-symbol">{m?.info?.symbol ?? symbol ?? "—"}</span>
        <span className="spacer" />
        <label className="inline-field" htmlFor={`${id}-min`}>
          <span className="muted">Size ≥</span>
          <input
            id={`${id}-min`}
            className="input input-xs num"
            inputMode="numeric"
            value={minSize}
            onChange={(e) => {
              setMinSize(e.target.value.replace(/\D/g, ""));
            }}
          />
        </label>
      </div>
      <div className="tape-cols tape-th" aria-hidden="true">
        <span>Time {zoneAbbreviation(zone)}</span>
        <span>Price</span>
        <span>Size</span>
      </div>
      {prints.length === 0 ? (
        <div className="empty-state">
          <div className="empty-title">No prints</div>
          <div className="muted">
            {symbol === null
              ? "No symbol in this link group."
              : m?.prints.length
                ? `No prints of ${String(min)}+ yet.`
                : mdEmptyReason(status, m)}
          </div>
        </div>
      ) : (
        <ol className="tape-body" aria-label={`Time and sales ${symbol ?? ""}`}>
          {prints.map((p) => {
            const side = p.aggressor === 1 ? "buy" : p.aggressor === 2 ? "sell" : "none";
            return (
              <li key={p.id} className={`tape-cols tape-row tape-${side}`}>
                <span className="num">{formatTime(p.tsMs, zone)}</span>
                <span className="num">
                  <span aria-hidden="true" className="tape-glyph">
                    {side === "buy" ? "▲" : side === "sell" ? "▼" : "·"}
                  </span>
                  <span className="sr-only">{side === "buy" ? "buy" : side === "sell" ? "sell" : ""}</span>
                  {formatTicks(p.priceTicks, fmt)}
                </span>
                <span className="num">{p.size}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
